import type { AgentMessage } from "@elyracode/agent-core";
import type { AssistantMessage, Model } from "@elyracode/ai";
import type { CacheWarmingSettings } from "./settings-manager.js";

/**
 * Keeps the provider's prompt cache warm while the user is idle.
 *
 * Anthropic's default cache TTL is 5 minutes from the last read or write. After a turn ends,
 * the warmer waits `delayMs`, checks that nothing has changed, and asks the session to send a
 * minimal request that replays the same prefix (system, tools, history) with `max_tokens: 1`.
 * That read refreshes the TTL for one cache-read price. It repeats up to `maxPings` times,
 * then gives up until the next real turn.
 *
 * The warmer only decides *when*; the session owns *how* (`ping`) so this stays free of
 * agent internals and is easy to test with fake timers.
 */

export interface CacheWarmerDeps {
	getSettings: () => CacheWarmingSettings;
	/** True when the agent is not streaming, compacting, or otherwise busy. */
	isIdle: () => boolean;
	getModel: () => Model<string> | undefined;
	/** Resolved credential for the model, used to exclude OAuth (subscription) accounts. */
	getApiKey: (model: Model<string>) => Promise<string | undefined>;
	getMessages: () => readonly AgentMessage[];
	/** Perform the warm request. Errors are caught and end the current idle period. */
	ping: (signal: AbortSignal) => Promise<void>;
	/** Optional observer for tests and diagnostics. */
	onEvent?: (event: CacheWarmerEvent) => void;
}

export type CacheWarmerSkipReason =
	| "disabled"
	| "busy"
	| "no-model"
	| "unsupported-model"
	| "no-api-key"
	| "oauth"
	| "small-prefix"
	| "messages-changed"
	| "max-pings";

export type CacheWarmerEvent =
	| { type: "armed"; delayMs: number; ping: number }
	| { type: "ping-start"; ping: number }
	| { type: "ping-end"; ping: number; ok: boolean; error?: string }
	| { type: "skipped"; reason: CacheWarmerSkipReason }
	| { type: "disarmed" };

/** Only Anthropic's Messages API exposes the TTL-refresh-on-read behaviour this relies on. */
export function supportsCacheWarming(model: Model<string> | undefined): model is Model<string> {
	return model?.api === "anthropic-messages";
}

/**
 * Anthropic OAuth tokens (Claude subscriptions) are billed against rate limits rather than
 * dollars, so a warm ping spends quota the user probably cares more about than cache misses.
 */
export function isOAuthApiKey(apiKey: string): boolean {
	return apiKey.includes("sk-ant-oat");
}

/**
 * Size of the prefix the next request would replay, taken from the last assistant message's
 * usage. `input + cacheRead + cacheWrite` is the full prompt the provider saw for that call.
 */
export function estimatePrefixTokens(messages: readonly AgentMessage[]): number {
	for (let i = messages.length - 1; i >= 0; i--) {
		const msg = messages[i];
		if (msg.role === "assistant") {
			const usage = (msg as AssistantMessage).usage;
			return (usage.input ?? 0) + (usage.cacheRead ?? 0) + (usage.cacheWrite ?? 0);
		}
	}
	return 0;
}

/** Identity of the history at arm time, so a ping can detect that the tail moved. */
function historyFingerprint(messages: readonly AgentMessage[]): string {
	const last = messages[messages.length - 1];
	return `${messages.length}:${last?.role ?? ""}:${last?.timestamp ?? 0}`;
}

export class CacheWarmer {
	private timer: ReturnType<typeof setTimeout> | undefined;
	private abort: AbortController | undefined;
	private pingsThisIdle = 0;
	private fingerprint = "";
	private disposed = false;

	constructor(private readonly deps: CacheWarmerDeps) {}

	/**
	 * Start (or restart) the idle countdown. Call after a real turn ends.
	 * Resets the per-idle ping counter.
	 */
	arm(): void {
		this.disarm(false);
		this.pingsThisIdle = 0;
		this.schedule();
	}

	/** Cancel any pending or in-flight ping. Call when a turn starts, the model changes, or on dispose. */
	disarm(emit = true): void {
		if (this.timer !== undefined) {
			clearTimeout(this.timer);
			this.timer = undefined;
		}
		if (this.abort) {
			this.abort.abort();
			this.abort = undefined;
		}
		if (emit) this.deps.onEvent?.({ type: "disarmed" });
	}

	dispose(): void {
		this.disposed = true;
		this.disarm(false);
	}

	/** Whether a ping is scheduled or running. */
	get isArmed(): boolean {
		return this.timer !== undefined || this.abort !== undefined;
	}

	private schedule(): void {
		if (this.disposed) return;
		const settings = this.deps.getSettings();
		if (!settings.enabled) {
			this.deps.onEvent?.({ type: "skipped", reason: "disabled" });
			return;
		}
		if (this.pingsThisIdle >= settings.maxPings) {
			this.deps.onEvent?.({ type: "skipped", reason: "max-pings" });
			return;
		}
		// Cheap checks up front so we do not hold a timer for a session that can never warm.
		const model = this.deps.getModel();
		if (!model) {
			this.deps.onEvent?.({ type: "skipped", reason: "no-model" });
			return;
		}
		if (!supportsCacheWarming(model)) {
			this.deps.onEvent?.({ type: "skipped", reason: "unsupported-model" });
			return;
		}
		if (estimatePrefixTokens(this.deps.getMessages()) < settings.minPrefixTokens) {
			this.deps.onEvent?.({ type: "skipped", reason: "small-prefix" });
			return;
		}

		this.fingerprint = historyFingerprint(this.deps.getMessages());
		const ping = this.pingsThisIdle + 1;
		this.deps.onEvent?.({ type: "armed", delayMs: settings.delayMs, ping });
		this.timer = setTimeout(() => {
			this.timer = undefined;
			void this.fire(ping);
		}, settings.delayMs);
		// Never keep the process alive just to warm a cache.
		if (typeof this.timer === "object" && "unref" in this.timer) {
			this.timer.unref();
		}
	}

	private async fire(ping: number): Promise<void> {
		if (this.disposed) return;
		if (!this.deps.isIdle()) {
			this.deps.onEvent?.({ type: "skipped", reason: "busy" });
			return;
		}
		const model = this.deps.getModel();
		if (!supportsCacheWarming(model)) {
			this.deps.onEvent?.({ type: "skipped", reason: "unsupported-model" });
			return;
		}
		if (historyFingerprint(this.deps.getMessages()) !== this.fingerprint) {
			// Something appended to history without a turn (e.g. a system message). The old
			// prefix is still cached, but re-arm so the ping replays the current tail.
			this.deps.onEvent?.({ type: "skipped", reason: "messages-changed" });
			this.schedule();
			return;
		}
		const apiKey = await this.deps.getApiKey(model);
		if (!apiKey) {
			this.deps.onEvent?.({ type: "skipped", reason: "no-api-key" });
			return;
		}
		if (isOAuthApiKey(apiKey)) {
			this.deps.onEvent?.({ type: "skipped", reason: "oauth" });
			return;
		}

		this.abort = new AbortController();
		const signal = this.abort.signal;
		this.deps.onEvent?.({ type: "ping-start", ping });
		let ok = false;
		let error: string | undefined;
		try {
			await this.deps.ping(signal);
			ok = !signal.aborted;
		} catch (err) {
			error = err instanceof Error ? err.message : String(err);
		} finally {
			if (this.abort?.signal === signal) this.abort = undefined;
		}
		if (signal.aborted || this.disposed) return;
		this.deps.onEvent?.({ type: "ping-end", ping, ok, error });
		if (!ok) return;

		this.pingsThisIdle = ping;
		this.schedule();
	}
}
